import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface BreadcrumbsContextValue {
  currentLabel: string | undefined;
  setCurrentLabel: (label: string | undefined) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue | null>(null);

interface BreadcrumbsProviderProps {
  children: ReactNode;
}

export function BreadcrumbsProvider({ children }: BreadcrumbsProviderProps) {
  const [currentLabel, setCurrentLabel] = useState<string | undefined>(
    undefined
  );

  const value = useMemo(
    () => ({ currentLabel, setCurrentLabel }),
    [currentLabel]
  );

  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
}

/**
 * Hook for pages to set a dynamic label for the current breadcrumb.
 * The label will be cleared when the component unmounts.
 *
 * @example
 * ```tsx
 * function CustomerDetail() {
 *   const { data: customer } = useGetCustomerById(customerId);
 *   useBreadcrumbLabel(customer?.name);
 *   // ...
 * }
 * ```
 */
export function useBreadcrumbLabel(label: string | undefined) {
  const context = useContext(BreadcrumbsContext);

  if (!context) {
    throw new Error(
      'useBreadcrumbLabel must be used within BreadcrumbsProvider'
    );
  }

  const { setCurrentLabel } = context;

  useEffect(() => {
    setCurrentLabel(label);
    return () => setCurrentLabel(undefined);
  }, [label, setCurrentLabel]);
}

/**
 * Hook to get the current breadcrumb label override.
 * Used internally by the Breadcrumbs component.
 */
export function useBreadcrumbsContext() {
  const context = useContext(BreadcrumbsContext);

  if (!context) {
    throw new Error(
      'useBreadcrumbsContext must be used within BreadcrumbsProvider'
    );
  }

  return context;
}
